<template>
  <article class="product">
    <header id="header" class="container product__header pb-y-gutter">
      <div class="product__wrap">
        <h1 class="text-center product__title lg:text-left heading">
          {{ title }}
        </h1>
        <h2
          v-if="description"
          class="pt-1 text-center product__description lg:text-left heading--sm max-w-200 pb-1/2em mb-1/2em"
        >
          {{ description }}
        </h2>
        <client-only>
          <fade appear>
            <div class="product__form">
              <add-product-to-cart
                v-if="shopifyProduct"
                v-bind="shopifyProduct"
                :amazon-link="amazonLink"
                :amazon-cta="amazonCta"
                :title="title"
                :path="path"
                :category="category"
                :is-canned="isCanned"
                :default-variant="defaultVariant"
                :product="shopifyProduct"
                :subscription="subscription"
                :subscription-skus="shopify.subscriptionSkus"
                :subscription-notice="shopify.subscriptionNotice"
                :single-purchase-notice="shopify.singlePurchaseNotice"
                :shipping-notice="shopify.shippingNotice"
                class="z-2above"
              />
            </div>
          </fade>
        </client-only>
        <div class="product__hero">
          <hero-gallery
            v-if="hasHeroGallery"
            :background-colors="product.colors?.backgroundColors"
            :slides="heroGallery"
          />
          <float v-else-if="thumb?.image" tag="hero" :default-media="thumb" />
        </div>
      </div>
    </header>
    <div id="content">
      <template v-if="showTestimonials && testimonials">
        <page-section v-if="testimonials.length > 0" centered class="pt-0">
          <testimonials :testimonials="testimonials" />
        </page-section>
      </template>
      <template v-if="showPress && featuredPress && featuredPress.length > 0">
        <page-section :title="pressTitle" centered>
          <Press :press="featuredPress" />
        </page-section>
      </template>
      <slot name="after-header" />
      <color-section
        :background-colors="[computedBackgroundColor]"
        :text-color="getColor('cloud-burst')"
      >
        <slot name="before-text" />
        <template v-if="features && features.list">
          <page-section :cta="features.link" :title="features.title">
            <rich-text
              class="container rich-text--alt-list sm:text-lg lg:text-xl 2xl:text-2xl"
            >
              <div class="text-center product__feature-list">
                <ul>
                  <li v-for="feat in features.list" :key="feat._key">
                    {{ feat }}
                  </li>
                </ul>
              </div>
            </rich-text>
          </page-section>
        </template>
        <featured-product
          v-if="filteredRelatedProducts && filteredRelatedProducts.featured"
          :product="filteredRelatedProducts.featured"
        />
        <template v-if="relatedSocialMedia && relatedSocialMedia.socialMedia">
          <page-section
            v-if="relatedSocialMedia.socialMedia.length > 0"
            :title="relatedSocialMedia.title"
            class="relative z-base"
          >
            <social-media
              :social-media="relatedSocialMedia.socialMedia"
              class="container"
            />
          </page-section>
        </template>
        <template
          v-if="filteredRelatedProducts && filteredRelatedProducts.products"
        >
          <!-- there is a blank ASCII character for `title` here to trick title into rendering slot -->
          <page-section
            v-if="filteredRelatedProducts.products.length > 0"
            cta-title="Shop All"
            title="‎"
            :cta="{
              to:
                relatedProductsTitleLink.url ||
                relatedProductsTitleLink.page ||
                `/shop/${category}`
            }"
          >
            <template #title>
              <recess-link
                v-if="
                  relatedProductsTitleLink.url || relatedProductsTitleLink.page
                "
                :sanity-link="relatedProductsTitleLink"
              />
              <span v-else> {{ relatedProductsTitleLink.title }} </span>
            </template>
            <products
              :products="filteredRelatedProducts.products"
              hover-images
              square
            />
          </page-section>
        </template>
      </color-section>
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex'
import AddProductToCart from '~/components/shop/add-product-to-cart.vue'
import SocialMedia from '~/components/social-media.vue'
import FeaturedProduct from '~/components/shop/products/featured-product.vue'
import Products from '~/components/shop/products/products.vue'
import Press from '~/components/press/press.vue'
import getCBDFilter from '~/util/get-cbd-filter'

export default {
  name: 'ProductDetail',

  components: {
    AddProductToCart,
    SocialMedia,
    FeaturedProduct,
    Products,
    Press
  },

  props: {
    title: {
      default: null,
      type: String
    },
    path: {
      default: null,
      type: String
    },
    category: {
      default: null,
      type: String
    },
    product: {
      default: () => ({}),
      type: Object
    },
    shopify: {
      default: () => ({}),
      type: Object
    },
    shopifyProduct: {
      default: () => ({}),
      type: Object
    },
    description: {
      default: null,
      type: String
    },
    heroGallery: {
      default: null,
      type: Array
    },
    thumb: {
      default: null,
      type: Object
    },
    gallery: {
      default: null,
      type: Object
    },
    featuredPress: {
      required: true,
      type: Array
    },
    testimonials: {
      default: null,
      type: Array
    },
    profile: {
      default: null,
      type: Object
    },
    relatedSocialMedia: {
      default: null,
      type: Object
    },
    features: {
      default: null,
      type: Object
    },
    relatedProductsTitleLink: {
      type: Object,
      default: () => ({
        title: "but wait, there's more"
      })
    },
    relatedProducts: {
      default: null,
      type: Object
    },
    isCanned: {
      type: Boolean,
      default: false
    },
    useFlavorSelector: {
      type: Boolean,
      default: false
    },
    amazonLink: {
      type: String,
      required: false,
      default: undefined
    },
    amazonCta: {
      type: String,
      required: false,
      default: undefined
    },
    showTestimonials: {
      type: Boolean,
      default: false
    },
    showPress: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // also defined in pages/index.vue
      pressTitle: 'Recess in the press',
      nonCBDSite: process.env.nonCBDSite,
      isCBDSafe: getCBDFilter(process.env.nonCBDSite)
    }
  },

  computed: {
    ...mapGetters('shop', {
      subscriptionById: 'subscriptionById'
    }),
    filteredRelatedProducts() {
      if (!this.relatedProducts) {
        return null
      }

      return {
        featured:
          this.nonCBDSite && this.relatedProducts.featured?.cbd
            ? null
            : this.relatedProducts.featured,
        products: this.relatedProducts.products.filter(this.isCBDSafe)
      }
    },
    /**
     * Returns a variant that matches the first default sku
     * if one is set. If not, returns the first product variant
     * @function
     */
    defaultVariant() {
      let variant
      const shopify = this.shopify
      if (!shopify) {
        return undefined
      }
      const product = this.shopifyProduct
      const defaultSku = shopify.defaultSku && shopify.defaultSku.sku

      if (product && defaultSku) {
        // find single-purchase product variant matching the sku regardless
        // of subscription if no subscription variant was found
        if (!variant) {
          variant = product.variants.edges.find(
            ({ node }) => node.sku === defaultSku
          )
        }
      } else if (product) {
        variant = product.variants.edges[0]
      }
      if (variant) {
        variant = variant.node
      }
      return variant
    },
    hasHeroGallery() {
      return this.heroGallery !== null && this.heroGallery.length > 0
    },
    subscription() {
      const shopify = this.shopify
      if (!shopify || !shopify.subscription) {
        return undefined
      }
      return this.subscriptionById(shopify.subscription.id)
    },
    isUnavailable() {
      return !this.shopifyProduct || !this.defaultVariant?.availableForSale
    },
    computedBackgroundColor() {
      return this.product?.colorSectionBackground
        ? this.getBackgroundColor(this.product.colorSectionBackground)
        : this.getColor('apricot')
    }
  }
}
</script>

<style>
.product__wrap {
  display: grid;
  grid-auto-rows: min-content;
  grid-template-areas: 'title' 'description' 'hero' 'form';
  grid-template-columns: minmax(0, 1fr);
}

.product__wrap > * {
  min-height: 0;
}

.product__title {
  grid-area: title;
}

.product__description {
  grid-area: description;
  margin-left: 0.08em;
}

.product__form {
  grid-area: form;
}

.product__hero {
  grid-area: hero;
  margin-bottom: 2em;
  order: -1;
  text-align: center;
  width: 100%;
}

.product__feature-list {
  display: flex;
}

.product__feature-list ul {
  columns: auto !important;
}

.rich-text .product__feature-list ul:not([class]) li:nth-child(odd)::before {
  content: '←';
}

@screen lg {
  .product__wrap {
    grid-column-gap: var(--x-gutter-sm);
    grid-template-areas: 'title hero' 'description hero' 'form hero';
    grid-template-columns: minmax(0, 64%) minmax(0, 34%);
    grid-template-rows: min-content min-content 1fr;
  }
}
</style>
